import { Check, X, Minus, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const COLS = [
  { key: "portales", label: "Portales", sub: "Coches.net · AutoScout · Wallapop" },
  { key: "comercial", label: "Comercial interno", sub: "Una persona de tu equipo" },
  { key: "agencia", label: "Agencia genérica", sub: "Marketing sin especializar" },
];

const ROWS = [
  {
    label: "Qué recibes",
    portales: { t: "Un anuncio más entre cientos." },
    comercial: { t: "Tiempo de una persona, sin más alcance." },
    agencia: { t: "Anuncios y gestión de redes." },
    fuelkers: { t: "Contenido, marca, posicionamiento y captación trabajando juntos." },
  },
  {
    label: "Calidad del contacto",
    portales: { t: "Fríos. Comparan precio antes de conocerte." },
    comercial: { t: "Depende de su cartera y su red." },
    agencia: { t: "Leads sin filtrar. Curiosos incluidos." },
    fuelkers: { t: "Filtrados antes de llegarte. Solo hablas con intención real." },
  },
  {
    label: "Confianza antes de la llamada",
    portales: { t: "Ninguna. El cliente no te conoce.", mark: "x" },
    comercial: { t: "Personal, pero no escala.", mark: "neutral" },
    agencia: { t: "Ninguna. Solo ven un anuncio.", mark: "x" },
    fuelkers: { t: "Ya confían en ti antes de escribirte.", mark: "check" },
  },
  {
    label: "Contenido y marca propia",
    portales: { t: "No.", mark: "x" },
    comercial: { t: "No.", mark: "x" },
    agencia: { t: "A veces, sin estrategia detrás.", mark: "neutral" },
    fuelkers: { t: "Sí. Con intención y sistema.", mark: "check" },
  },
  {
    label: "A largo plazo",
    portales: { t: "Sigues pagando por cada visita, siempre." },
    comercial: { t: "Si se va esa persona, se va su cartera." },
    agencia: { t: "Dependes por completo del proveedor." },
    fuelkers: { t: "Construyes un activo propio: marca, contenido, comunidad." },
  },
  {
    label: "Si no funciona",
    portales: { t: "Nada. Es autoservicio." },
    comercial: { t: "Nada. Es su sueldo, funcione o no." },
    agencia: { t: "Contrato cerrado. Resultado genérico." },
    fuelkers: { t: "Sin letra pequeña: si no te hacemos crecer, seguimos trabajando sin coste." },
  },
];

const Mark = ({ mark }) => {
  if (mark === "x") return <X className="w-4 h-4 text-[#ff4d4d] flex-none mt-0.5" strokeWidth={3} />;
  if (mark === "check") return <Check className="w-4 h-4 text-[#00A0A0] flex-none mt-0.5" strokeWidth={3} />;
  if (mark === "neutral") return <Minus className="w-4 h-4 text-white/30 flex-none mt-0.5" strokeWidth={3} />;
  return null;
};

export default function TablaComparativa() {
  return (
    <section id="comparativa-tabla" data-testid="comparativa-tabla-section" className="relative py-24 lg:py-32 border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(90% 70% at 70% 0%, rgba(0,160,160,.07), transparent 55%)" }} />
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
        <Reveal>
          <div className="max-w-3xl mb-12 lg:mb-16">
            <div className="font-stencil text-xs text-[#00A0A0] tracking-widest mb-4">07 / COMPARATIVA</div>
            <h2 className="font-heading text-white text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
              No todas las formas de conseguir
              <br />
              <span className="font-stencil text-[#00A0A0]">clientes son iguales.</span>
            </h2>
            <p className="mt-6 text-white/60 max-w-2xl font-body text-base lg:text-lg leading-relaxed">
              Así se compara un sistema que construye confianza con las alternativas que ya conoces.
            </p>
          </div>
        </Reveal>

        {/* Desktop table */}
        <Reveal>
          <div className="hidden lg:block">
            <div className="grid grid-cols-[1.1fr_1fr_1fr_1fr_1.2fr] gap-px bg-white/[0.06] rounded-2xl overflow-hidden border border-white/10">
              {/* header row */}
              <div className="bg-[#0a0e0d] p-5" />
              {COLS.map((c) => (
                <div key={c.key} className="bg-[#0a0e0d] p-5">
                  <div className="font-stencil text-sm text-white uppercase tracking-wide">{c.label}</div>
                  <div className="text-[11px] text-white/40 font-body mt-1 leading-snug">{c.sub}</div>
                </div>
              ))}
              <div className="bg-[#08201d] p-5 border-t-2 border-[#00A0A0]" style={{ boxShadow: "inset 0 0 40px -20px rgba(0,160,160,.8)" }}>
                <div className="font-stencil text-sm text-[#00C8C8] uppercase tracking-wide">Sistema Fuelkers</div>
                <div className="text-[11px] text-[#00A0A0]/70 font-body mt-1 leading-snug">Lo que hacemos por ti</div>
              </div>

              {/* body rows */}
              {ROWS.map((row, ri) => (
                <RowGroup key={ri} row={row} />
              ))}
            </div>
          </div>
        </Reveal>

        {/* Mobile: stacked cards */}
        <div className="lg:hidden space-y-4">
          {ROWS.map((row, ri) => (
            <Reveal key={ri} delay={ri * 40}>
              <div className="glass p-5">
                <div className="font-stencil text-sm text-white uppercase tracking-wide mb-4">{row.label}</div>
                <div className="space-y-3">
                  {COLS.map((c) => (
                    <div key={c.key} className="flex items-start gap-2 text-sm">
                      <span className="text-white/40 font-body text-xs w-32 flex-none pt-0.5">{c.label}</span>
                      <span className="text-white/70 font-body text-[13px] flex items-start gap-1.5">
                        <Mark mark={row[c.key].mark} />
                        {row[c.key].t}
                      </span>
                    </div>
                  ))}
                  <div className="flex items-start gap-2 text-sm pt-3 mt-1 border-t border-[#00A0A0]/25">
                    <span className="text-[#00C8C8] font-stencil text-xs w-32 flex-none pt-0.5 uppercase">Fuelkers</span>
                    <span className="text-white font-body text-[13px] flex items-start gap-1.5 font-semibold">
                      <Mark mark={row.fuelkers.mark} />
                      {row.fuelkers.t}
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Closing CTA */}
        <Reveal>
          <div className="mt-14 lg:mt-16 glass p-7 sm:p-9 text-center max-w-4xl mx-auto relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#00A0A0]/15 rounded-full blur-3xl pointer-events-none" />
            <p className="relative text-white/85 font-body text-base lg:text-lg leading-relaxed max-w-2xl mx-auto">
              No te pedimos que elijas a ciegas. En una llamada de 30 minutos revisamos tu situación y te decimos con claridad qué mejoraríamos
              <span className="text-[#00A0A0] font-semibold"> — encajemos o no.</span>
            </p>
            <a
              href="#contacto"
              data-testid="tabla-cta"
              className="relative mt-7 h-14 btn-angular bg-[#FFD700] hover:bg-[#FFE34D] text-black text-xs sm:text-sm font-stencil uppercase tracking-widest rounded-none group inline-flex items-center justify-center gap-2 px-8 transition-colors"
              style={{ boxShadow: "5px 5px 0 #00A0A0" }}
            >
              RESERVAR LLAMADA
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function RowGroup({ row }) {
  return (
    <>
      <div className="bg-[#0a0e0d] p-5 flex items-center">
        <span className="font-stencil text-[13px] text-white uppercase tracking-wide leading-tight">{row.label}</span>
      </div>
      {COLS.map((c) => (
        <div key={c.key} className="bg-[#0a0e0d] p-5">
          <div className="flex items-start gap-2 text-[13.5px] text-white/65 font-body leading-snug">
            <Mark mark={row[c.key].mark} />
            <span>{row[c.key].t}</span>
          </div>
        </div>
      ))}
      <div className="bg-[#08201d] p-5" style={{ boxShadow: "inset 0 0 40px -24px rgba(0,160,160,.8)" }}>
        <div className="flex items-start gap-2 text-[13.5px] text-white font-body font-semibold leading-snug">
          <Mark mark={row.fuelkers.mark} />
          <span>{row.fuelkers.t}</span>
        </div>
      </div>
    </>
  );
}
